import ProductLoader from '@/_application/core/components/ui/loaders/product-loader';
import {ProductCard} from '@/_application/modules/products/components/cards/product-card';
import {ScrollToLoadMore} from '@/_application/modules/products/components/scroll-to-load-more';
import {useProductListView} from '@/_application/modules/products/provider/product-list-view.provider';
import {ProductListItemsQueryOptions} from '@/_application/modules/products/request/product-list.request';
import {ProductListItemsQuery} from '@/framework/gql/products.graphql';
import {useProductListItems} from '@/framework/product';
import rangeMap from '@/lib/range-map';
import {useRouter} from 'next/router';
import {useEffect, useMemo, useRef, useState} from 'react';
import NotFound from '@/_application/core/components/ui/not-found';
import {getImageAssetUrl} from '@/_application/core/utils/common';
import {PRODUCTS_PER_PAGE} from '@/framework/client/variables';
import {isParamsChanged} from '@/_application/modules/products/helpers/component-render';
import {SearchTracking} from '@/_application/modules/tracking/components/search-tracking';
import {PageType} from '@/_application/core/types';
import {generateContentFromProduct, generateCustomerInput} from '@/_application/modules/tracking/cdp/utils/input';
import {Product} from '@/_application/modules/products/model/product';
import {useSession} from 'next-auth/react';
import {Customer, ProductInterface} from '@/__generated__/__types__';
import {useSearchParams} from 'next/navigation';

type ProductCardListMobileProps = {
    initialProducts?: ProductListItemsQuery['products'];
    productListItemsQueryOptions?: ProductListItemsQueryOptions;
    handleScrollToLastProduct?: () => void;
};

export const ProductCardListMobile = ({
    initialProducts,
    productListItemsQueryOptions = {
        filter: {},
        sort: {},
        search: '',
        currentPage: 1,
        pageSize: PRODUCTS_PER_PAGE,
    } as ProductListItemsQueryOptions,
    handleScrollToLastProduct,
}: ProductCardListMobileProps) => {
    const query = useSearchParams();
    const {filter, sort, currentPage, pageSize, language, search} = useProductListView();
    const variables: ProductListItemsQueryOptions = {
        pageSize,
        currentPage,
        sort: sort,
        filter: filter ?? {},
        search,
        language,
    };
    const {fetchData, isLoading, loadMore, hasMore, products, pageInfo} = useProductListItems({
        ...variables,
        ...(query.get('category') && {categories: query.get('category')}),
        ...(query.get('text') && {name: query.get('text')}),
    });
    const {data: session} = useSession();
    const customer = session?.user as Customer;

    // Track if this is the first render
    const isFirstRender = useRef(true);

    // Track previous variables to detect actual user changes
    const prevVariablesRef = useRef<string | null>(null);

    useEffect(() => {
        const currentVariables = JSON.stringify(variables);

        // On first render, just store the variables and don't fetch (we have initialProducts)
        if (isFirstRender.current) {
            isFirstRender.current = false;
            prevVariablesRef.current = currentVariables;
            return;
        }

        // Only fetch if variables actually changed from previous render
        if (prevVariablesRef.current !== currentVariables) {
            prevVariablesRef.current = currentVariables;
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(variables)]);

    useEffect(() => {
        if (!pageInfo?.current_page || !pageInfo?.total_pages) {
            return;
        }

        if (pageInfo?.current_page === pageInfo?.total_pages) {
            handleScrollToLastProduct && handleScrollToLastProduct();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageInfo?.current_page, pageInfo?.total_pages]);

    const babySadImage = getImageAssetUrl('images/baby-sad.png');

    const productItems = useMemo(() => {
        // If we have fetched products (after user interaction), use them
        if (products?.items?.length) {
            return products.items;
        }

        // Otherwise use initial products from server
        return initialProducts?.items ?? [];
    }, [initialProducts, products]);

    const handleLoadMore = () => {
        //1: when page first render, we use products data from server, pageInfo is null
        //2: load more product and pageInfo is NULL -> call fetchData to fetch data page 2 -> pageInfo now has data
        //3: pageInfo has data, we can call loadMore (loadMore need pageInfo data) get products of next page
        if (pageInfo) {
            loadMore();
        } else {
            const page = productListItemsQueryOptions.currentPage ?? 1;
            fetchData(page + 1);
        }
    };

    if (!isLoading && !productItems.length) {
        return (
            <div className="h-screen items-center justify-center bg-white">
                <NotFound text="text-not-found" className="mx-auto pt-20" imageUrl={babySadImage} size="small" />
            </div>
        );
    }

    return (
        <div className="mx-4 w-100% pb-4 pt-4">
            {isLoading && !productItems.length ? (
                <div className="grid gap-4 grid-cols-2 sm:grid-cols-3">
                    {rangeMap(6, i => (
                        <ProductLoader key={i} uniqueKey={`product-${i}`} />
                    ))}
                </div>
            ) : (
                <>
                    {search && (
                        <SearchTracking
                            page={PageType.SEARCH}
                            input={{
                                search_string: search,
                                contents: productItems?.map(item => generateContentFromProduct(item as Product)) ?? [],
                                customer: generateCustomerInput(customer),
                            }}
                        />
                    )}

                    <div className="grid gap-4 grid-cols-2 sm:grid-cols-3">
                        {productItems.map((product, index) =>
                            // Currently only support 2 types
                            product && ['SimpleProduct', 'ConfigurableProduct'].includes(product.__typename ?? '') ? (
                                <ProductCard
                                    isFirstProductInList={index === 0}
                                    product={product as ProductInterface}
                                    key={`${(product as Product)?.id}`}
                                />
                            ) : (
                                <></>
                            ),
                        )}

                        {isLoading &&
                            rangeMap(pageSize ?? 6, i => <ProductLoader key={i} uniqueKey={`product-${i}`} />)}
                    </div>

                    <ScrollToLoadMore
                        onLoadMore={handleLoadMore}
                        pagePercent={80}
                        isHasMore={hasMore || !pageInfo}
                        isLoading={isLoading}
                    />
                </>
            )}
        </div>
    );
};
